import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Button } from "antd";
import { getGroupMembersByGroupNoApi } from "../service/profile";
import { applyJoinDeptApi, getDeptListSimpleApi, applyOutDeptApi } from "../service/team";
import { globalStore } from "../utils/globalStore";
import { InitService } from "../utils/initService";
import { useUserRole } from "./useUserRole";
import { useAlert } from "./useAlert";
import type { UserInfo } from "../types";
import { AutoModel } from "../components/common/AutoModel";
import {
  TeamFormBody,
  TEAM_MODAL_CONTENT_CLASS,
  teamFormTitle,
  type TeamFormBodyRef,
} from "../components/common/TeamFormBody";

export type UseOperatorTeamModalOptions = {
  /** 当前用户信息（与 globalStore 同步的一份即可） */
  userInfo: unknown;
  /** 刷新权限/用户数据后写回本地 state（如 NavBar）；仅依赖 globalStore 时可不传 */
  onUserInfoSynced?: (info: unknown) => void;
  /** 与壳层 `AlertMessage` 共用同一套提示（不传则 hook 内自建 useAlert，易与页面提示脱节） */
  showSuccess?: (msg: string) => void;
  showError?: (msg: string) => void;
};

export type OperatorTeamAction = {
  text: string;
  onClick: () => void;
};

/**
 * 操作者「加入团队 / 退出团队」（权限 `dept:apply:create`）。
 * 后端约定 `dept.id === 4` 为临时团队 / 待审核等：仅展示「加入团队」，不展示「退出团队」。
 */
export function useOperatorTeamModal(options: UseOperatorTeamModalOptions) {
  const { userInfo, onUserInfoSynced, showSuccess: showSuccessOpt, showError: showErrorOpt } = options;
  const { isOperator, updateUserRole } = useUserRole();
  const fallbackAlert = useAlert();
  const showSuccess = showSuccessOpt ?? fallbackAlert.showSuccess;
  const showError = showErrorOpt ?? fallbackAlert.showError;

  useEffect(() => {
    if (userInfo) {
      updateUserRole(userInfo as UserInfo);
    }
  }, [userInfo, updateUserRole]);

  const checkTeamPermission = useCallback((permission: string): boolean => {
    const permissions = globalStore.getPermissions();
    if (permissions && permissions.length > 0 && permissions.includes(permission)) {
      return true;
    }
    const menuList = globalStore.getMenuList();
    if (!menuList || menuList.length === 0) {
      return false;
    }
    const findMenuPermission = (menus: unknown[], targetPermission: string): boolean => {
      for (const menu of menus) {
        const m = menu as { meta?: { perms?: string }; children?: unknown[] };
        if (m.meta?.perms === targetPermission) {
          return true;
        }
        if (m.children && m.children.length > 0) {
          if (findMenuPermission(m.children, targetPermission)) {
            return true;
          }
        }
      }
      return false;
    };
    return findMenuPermission(menuList as unknown[], permission);
  }, []);

  const isOperatorOnly = useCallback((): boolean => {
    const u = userInfo as { roles?: unknown[] } | null | undefined;
    const roleKeys =
      u?.roles?.map((role: unknown) =>
        typeof role === "string"
          ? role
          : String((role as { role_key?: string; code?: string }).role_key || (role as { code?: string }).code || "")
      ) || [];

    const lower = roleKeys.map((k: string) => k.toLowerCase());
    const isAdmin = lower.some((key: string) => key === "admin" || key === "super_admin" || key === "administrator");
    if (isAdmin) return false;

    const isAssessorRole = lower.some((key: string) => key === "assessor" || key === "reviewer");
    if (isAssessorRole) return false;

    if (isOperator) {
      if (isAdmin || isAssessorRole) return false;
      return true;
    }

    return lower.some((key: string) => key === "operator");
  }, [userInfo, isOperator]);

  const hasAddGroupPermission = useCallback(
    () => checkTeamPermission("dept:apply:create"),
    [checkTeamPermission]
  );
  const hasOutGroupPermission = useCallback(
    () => checkTeamPermission("dept:apply:create"),
    [checkTeamPermission]
  );

  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamModalType, setTeamModalType] = useState<"create" | "join" | "exit">("create");
  const [teamModalLoading, setTeamModalLoading] = useState(false);
  const [currentTeamInfo, setCurrentTeamInfo] = useState<{ teamId: string; auditorName: string }>({
    teamId: "",
    auditorName: "",
  });
  const teamFormRef = useRef<TeamFormBodyRef>(null);

  const handleAddGroup = useCallback(() => {
    setTeamModalType("join");
    setShowTeamModal(true);
  }, []);

  const getCurrentTeamInfo = useCallback(async () => {
    try {
      const storedUserInfo = globalStore.getUserInfo();
      const u = userInfo as Record<string, unknown> | null | undefined;
      const su = storedUserInfo as Record<string, unknown> | null | undefined;
      const userDept =
        (su?.dept as { id?: number; name?: string } | undefined) ??
        (u?.dept as { id?: number; name?: string } | undefined);

      const getReviewerNamesFromDept = (deptLike: Record<string, unknown>): string => {
        const reviewers = (deptLike?.reviewers as unknown[]) || ((deptLike?.dept as Record<string, unknown>)?.reviewers as unknown[]) || [];
        if (!Array.isArray(reviewers) || reviewers.length === 0) return "";
        return reviewers
          .map((r: unknown) => {
            const o = r as { nickname?: string; username?: string };
            return (o.nickname || o.username || "").trim();
          })
          .filter((n: string) => !!n)
          .join(",");
      };

      if (!userDept || !userDept.id || userDept.id === 4) {
        const userGroups = (su?.groups as unknown[] | undefined) ?? (u?.groups as unknown[] | undefined);
        if (!userGroups || !Array.isArray(userGroups) || userGroups.length === 0) {
          return { teamId: "", auditorName: "" };
        }
        const g0 = userGroups[0] as { group_no?: string; team_id?: string };
        const teamId = g0?.group_no || g0?.team_id || "";
        if (!teamId) {
          return { teamId: "", auditorName: "" };
        }
        const members = await getGroupMembersByGroupNoApi(teamId);
        const assessors = members.filter((member) => member.role === "assessor");
        const auditorName =
          assessors.length > 0 ? assessors.map((assessor) => assessor.username).join(",") : "管理员";
        return { teamId, auditorName };
      }

      const ud = userDept as Record<string, unknown>;
      const teamName = String(ud.name ?? ud.deptName ?? ud.label ?? "").trim();
      // dept.reviewers / leaderNickname 已在登录时由 initService 通过 getMenuPermissionsApi 写入 globalStore，
      // 此处直接读取，避免重复请求引发循环。
      const reviewerNames = getReviewerNamesFromDept(ud);
      const auditorName =
        reviewerNames || String(ud.leaderNickname || "管理员");

      return { teamId: teamName, auditorName };
    } catch (error) {
      console.error("获取团队信息失败:", error);
      return { teamId: "", auditorName: "" };
    }
  }, [userInfo]);

  const handleOutGroup = useCallback(async () => {
    setTeamModalType("exit");
    const teamInfo = await getCurrentTeamInfo();
    setCurrentTeamInfo(teamInfo);
    setShowTeamModal(true);
  }, [getCurrentTeamInfo]);

  const handleTeamModalCancel = useCallback(() => {
    setShowTeamModal(false);
    setTeamModalLoading(false);
  }, []);

  const updateUserTeamStatus = useCallback(async () => {
    await InitService.initializeUserData();
    const updatedUserInfo = globalStore.getUserInfo();
    onUserInfoSynced?.(updatedUserInfo);
  }, [onUserInfoSynced]);

  const handleTeamModalConfirm = useCallback(
    async (teamId?: string, teamName?: string) => {
      setTeamModalLoading(true);
      try {
        if (teamModalType === "join") {
          if (!teamId) {
            showError("请输入团队名称");
            setTeamModalLoading(false);
            return;
          }
          try {
            const deptListResponse = await getDeptListSimpleApi();
            const deptList = Array.isArray(deptListResponse?.data) ? deptListResponse.data : [];
            const matchedDept = deptList.find((dept) => dept.name === teamId || dept.id.toString() === teamId);
            if (!matchedDept) {
              showError("未找到该团队，请检查团队名称是否正确");
              setTeamModalLoading(false);
              return;
            }
            const response = await applyJoinDeptApi(matchedDept.id);
            if (response && response.code === 0) {
              showSuccess("申请已提交，等待审核");
              setShowTeamModal(false);
              await updateUserTeamStatus();
            } else {
              showError(response?.msg || "申请失败，请重试");
            }
          } catch (error: unknown) {
            console.error("申请加入团队失败:", error);
            const err = error as { response?: { detail?: string; message?: string }; message?: string };
            let errorMessage = "申请失败，请重试";
            if (err?.response?.detail) errorMessage = err.response.detail;
            else if (err?.response?.message) errorMessage = err.response.message;
            else if (err?.message) errorMessage = err.message;
            showError(errorMessage);
          } finally {
            setTeamModalLoading(false);
          }
        } else if (teamModalType === "exit") {
          try {
            const response = await applyOutDeptApi();
            if (response && response.code === 0) {
              showSuccess("申请已提交，等待审核");
              setShowTeamModal(false);
              await updateUserTeamStatus();
            } else {
              showError(response?.msg || "申请失败，请重试");
            }
          } catch (error: unknown) {
            console.error("申请退出团队失败:", error);
            const err = error as { response?: { detail?: string; message?: string }; message?: string };
            let errorMessage = "申请失败，请重试";
            if (err?.response?.detail) errorMessage = err.response.detail;
            else if (err?.response?.message) errorMessage = err.response.message;
            else if (err?.message) errorMessage = err.message;
            showError(errorMessage);
          } finally {
            setTeamModalLoading(false);
          }
        } else if (teamModalType === "create") {
          console.log("创建团队:", teamName);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          showSuccess("成功创建团队");
          setShowTeamModal(false);
        }
      } catch (error: unknown) {
        console.error("团队操作失败:", error);
        const err = error as { message?: string };
        showError(err.message || "操作失败，请重试");
      } finally {
        setTeamModalLoading(false);
      }
    },
    [teamModalType, showError, showSuccess, updateUserTeamStatus]
  );

  const teamAction = useMemo((): OperatorTeamAction | null => {
    if (!isOperatorOnly()) return null;
    const hasAdd = hasAddGroupPermission();
    const hasOut = hasOutGroupPermission();
    const storedUserInfo = globalStore.getUserInfo();
    const u = userInfo as Record<string, unknown> | null | undefined;
    const su = storedUserInfo as Record<string, unknown> | null | undefined;
    /** 以 globalStore 为准，避免 React userInfo 滞后盖住 store（侧栏团队文案延迟） */
    const userDept = (su?.dept as { id?: number } | undefined) ?? (u?.dept as { id?: number } | undefined);
    const userGroups = (su?.groups as unknown[] | undefined) ?? (u?.groups as unknown[] | undefined);
    /** 已进正式团队（非临时 dept id=4）或仅有 groups 兜底 */
    const isInRealTeam =
      (userDept != null && userDept.id != null && userDept.id !== 4) ||
      (!userDept && userGroups && userGroups.length > 0);
    /** 后端约定：dept.id===4 为临时团队 / 待审核等：只展示「加入团队」（需权限），不展示「退出团队」 */
    const isTempOrPendingDept = userDept != null && userDept.id === 4;

    if (isInRealTeam && hasOut) {
      return { text: "退出团队", onClick: handleOutGroup };
    }
    if (isTempOrPendingDept) {
      return hasAdd ? { text: "加入团队", onClick: handleAddGroup } : null;
    }
    if (!isInRealTeam && hasAdd) {
      return { text: "加入团队", onClick: handleAddGroup };
    }
    return null;
  }, [userInfo, isOperatorOnly, hasAddGroupPermission, hasOutGroupPermission, handleOutGroup, handleAddGroup]);

  const isOperatorOnlyUser = useMemo(() => isOperatorOnly(), [isOperatorOnly]);

  const teamModal: ReactNode = (
    <AutoModel
      open={showTeamModal}
      onClose={() => {
        teamFormRef.current?.reset();
        handleTeamModalCancel();
      }}
      title={teamFormTitle(teamModalType)}
      size="sm"
      maskClosable={false}
      contentClassName={TEAM_MODAL_CONTENT_CLASS}
      footerRight={<>
        <Button
          onClick={() => {
            teamFormRef.current?.reset();
            handleTeamModalCancel();
          }}
        >
          取消
        </Button>
        <Button type="primary" loading={teamModalLoading} onClick={() => teamFormRef.current?.submit()}>
          确认
        </Button>
      </>}
    >
      <TeamFormBody
        ref={teamFormRef}
        visible={showTeamModal}
        type={teamModalType}
        onConfirm={handleTeamModalConfirm}
        currentTeamId={currentTeamInfo.teamId}
        auditorName={currentTeamInfo.auditorName}
      />
    </AutoModel>
  );

  return { teamAction, teamModal, isOperatorOnly: isOperatorOnlyUser };
}
